// Plan detail — shows a Travel/Discover task's AI-built itinerary. Subtasks
// render by `kind` (flight/hotel/activity/meal/transit/place/generic), grouped
// by trip day. Bookable kinds can be confirmed (locking them so the AI re-plan
// preserves them). "Build plan" / "Regenerate" call the backend breakdown.
//
// Every mutating call may return a `replan` payload (the refreshed itinerary);
// we apply it so downstream items adjust, exactly like the mobile app.

import React, { useState } from 'react';
import {
  X, Circle, CheckCircle2, Plane, BedDouble, Map, Utensils, Bus, MapPin,
  ExternalLink, Trash2, Sparkles, RefreshCw,
} from 'lucide-react';
import {
  generateBreakdown, recordBooking, updateSubtask, deleteSubtask,
} from '../services/breakdownService';
import './PlanScreen.css';

const KIND_ICON = {
  generic: Circle, flight: Plane, hotel: BedDouble, activity: Map,
  meal: Utensils, transit: Bus, place: MapPin,
};
const BOOKABLE = ['flight', 'hotel', 'meal', 'transit'];
const PROVIDERS = ['amadeus', 'booking', 'expedia', 'kayak', 'opentable', 'seatgeek', 'manual'];

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function PlanDetail({ task, onClose, onChanged }) {
  const [subtasks, setSubtasks] = useState(task.subtasks || []);
  const [brief, setBrief] = useState(task.brief || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [bookingFor, setBookingFor] = useState(null);

  const isDiscover = (task.category || '').toLowerCase() === 'discover';
  const meta = task.category_metadata || {};
  const needsDestination = !isDiscover && !meta.destination;

  const propagate = (nextSubs, nextBrief) => {
    setSubtasks(nextSubs);
    if (nextBrief !== undefined) setBrief(nextBrief);
    onChanged({ ...task, subtasks: nextSubs, brief: nextBrief !== undefined ? nextBrief : brief });
  };

  const applyReplan = (replan) => {
    if (replan && Array.isArray(replan.subtasks)) {
      propagate(replan.subtasks, replan.brief);
      return true;
    }
    return false;
  };

  const build = async (force) => {
    if (needsDestination) { setError('Add a destination first (edit the trip).'); return; }
    setBusy(true); setError('');
    try {
      const res = await generateBreakdown(task.id, { cause: 'first_breakdown', force });
      propagate(res.subtasks || [], res.brief || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not build the plan. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const toggleComplete = async (st) => {
    const next = subtasks.map((s) => (s.id === st.id ? { ...s, completed: !s.completed } : s));
    setSubtasks(next); // optimistic
    try {
      const res = await updateSubtask(st.id, { completed: !st.completed });
      if (!applyReplan(res.replan)) onChanged({ ...task, subtasks: next, brief });
    } catch (_) {
      setSubtasks(subtasks); // revert
    }
  };

  const remove = async (st) => {
    if (!window.confirm('Remove this step?')) return;
    const next = subtasks.filter((s) => s.id !== st.id);
    setSubtasks(next);
    try {
      const res = await deleteSubtask(st.id);
      if (!applyReplan(res.replan)) onChanged({ ...task, subtasks: next, brief });
    } catch (_) {
      setSubtasks(subtasks);
    }
  };

  const submitBooking = async (fields) => {
    const res = await recordBooking(bookingFor.id, fields);
    setBookingFor(null);
    if (!applyReplan(res.replan)) {
      // No re-plan returned — at least lock the one we booked.
      const next = subtasks.map((s) =>
        (s.id === res.subtask_id ? { ...s, booking_data: res.booking, is_locked: true } : s));
      propagate(next);
    }
  };

  // group by day_index (null → "Itinerary")
  const groups = {};
  subtasks.forEach((s) => {
    const key = s.day_index == null ? 'none' : String(s.day_index);
    (groups[key] = groups[key] || []).push(s);
  });
  const groupKeys = Object.keys(groups).sort((a, b) => {
    if (a === 'none') return 1; if (b === 'none') return -1;
    return Number(a) - Number(b);
  });

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-detail" onClick={(e) => e.stopPropagation()}>
        <div className="plan-modal-head">
          <div>
            <span className="plan-detail-eyebrow">{isDiscover ? 'Discover' : 'Travel'}</span>
            <h2>{task.task_text}</h2>
          </div>
          <button className="plan-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="plan-detail-body">
          {brief && <p className="plan-brief">{brief}</p>}
          {error && <div className="plan-error">{error}</div>}

          {subtasks.length === 0 ? (
            <div className="plan-build-cta">
              <Sparkles size={26} />
              <p>{needsDestination
                ? 'Add a destination to this trip, then build the plan.'
                : 'Let the AI turn this into a step-by-step plan.'}</p>
              <button className="plan-btn-primary" disabled={busy || needsDestination} onClick={() => build(false)}>
                {busy ? 'Building…' : 'Build plan'}
              </button>
            </div>
          ) : (
            <>
              {groupKeys.map((gk) => (
                <section key={gk} className="plan-day-group">
                  {gk !== 'none' && <h3 className="plan-day-title">Day {Number(gk) + 1}</h3>}
                  <ul className="plan-sub-list">
                    {groups[gk].map((st, i) => (
                      <SubtaskRow
                        key={st.id}
                        index={i}
                        st={st}
                        onToggle={() => toggleComplete(st)}
                        onRemove={() => remove(st)}
                        onBook={() => setBookingFor(st)}
                      />
                    ))}
                  </ul>
                </section>
              ))}

              <button className="plan-regen" disabled={busy} onClick={() => build(true)}>
                <RefreshCw size={15} /> {busy ? 'Regenerating…' : 'Regenerate with AI'}
              </button>
            </>
          )}
        </div>
      </div>

      {bookingFor && (
        <BookingModal subtask={bookingFor} onClose={() => setBookingFor(null)} onSubmit={submitBooking} />
      )}
    </div>
  );
}

function SubtaskRow({ st, index, onToggle, onRemove, onBook }) {
  const [open, setOpen] = useState(false);
  const Icon = KIND_ICON[st.kind] || Circle;
  const locked = st.is_locked || !!st.booking_data;
  const bookable = BOOKABLE.includes(st.kind) && !locked;
  const time = [fmtTime(st.start_time), fmtTime(st.end_time)].filter(Boolean).join(' → ');

  const bd = st.booking_data || {};
  const detailRows = [];
  if (bd.confirmation_code) detailRows.push(['Confirmation', bd.confirmation_code]);
  if (bd.provider) detailRows.push(['Provider', bd.provider]);
  if (bd.total_cost) detailRows.push(['Cost', `${bd.total_cost}${bd.currency ? ` ${bd.currency}` : ''}`]);
  const note = bd.notes || st.notes;
  const hasDetails = detailRows.length > 0 || !!note;

  const status = st.completed ? 'done' : locked ? 'booked' : 'pending';
  const statusLabel = st.completed ? 'Done' : locked ? 'Booked' : 'Pending';

  return (
    <li className={`plan-sub-row${st.completed ? ' done' : ''}`} style={{ animationDelay: `${Math.min(index || 0, 8) * 45}ms` }}>
      <div className="plan-sub-main">
        <button className="plan-sub-check" onClick={onToggle} aria-label="Toggle done">
          {st.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
        </button>
        <span className={`plan-sub-kind kind-${st.kind}`}><Icon size={16} /></span>
        <div
          className={`plan-sub-body${hasDetails ? ' has-details' : ''}`}
          onClick={() => hasDetails && setOpen((o) => !o)}
        >
          <div className="plan-sub-top">
            <span className="plan-sub-text">{st.text}</span>
            <span className={`plan-sub-status st-${status}`}>{statusLabel}</span>
          </div>
          {time && <span className="plan-sub-meta">{time}</span>}
        </div>
        <div className="plan-sub-actions">
          {st.deep_link_url && (
            <a className="plan-sub-link" href={st.deep_link_url} target="_blank" rel="noopener noreferrer" title="Open">
              <ExternalLink size={15} />
            </a>
          )}
          {bookable && (
            <button className="plan-sub-book" onClick={onBook}>Mark booked</button>
          )}
          <button className="plan-sub-del" onClick={onRemove} aria-label="Remove"><Trash2 size={15} /></button>
        </div>
      </div>

      {open && hasDetails && (
        <div className="plan-sub-detail">
          {detailRows.map(([k, v]) => (
            <div className="plan-sub-detail-row" key={k}><span>{k}</span><strong>{v}</strong></div>
          ))}
          {note && <p className="plan-sub-detail-note">{note}</p>}
        </div>
      )}
    </li>
  );
}

function BookingModal({ subtask, onClose, onSubmit }) {
  const [confirmation, setConfirmation] = useState('');
  const [provider, setProvider] = useState('manual');
  const [notes, setNotes] = useState('');
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!confirmation.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        confirmation_code: confirmation.trim(),
        provider, notes: notes.trim(), total_cost: cost.trim(), currency: currency.trim(),
      });
    } catch (_) {
      window.alert('Could not save the booking. Try again.');
      setSaving(false);
    }
  };

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="plan-modal-head">
          <h2>Confirm booking</h2>
          <button className="plan-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="plan-modal-body">
          <p className="plan-sub-text" style={{ marginTop: 0 }}>{subtask.text}</p>
          <label className="plan-field">
            <span className="plan-field-label">Confirmation #<span className="plan-req"> *</span></span>
            <input className="plan-input" value={confirmation} placeholder="AA-1142-XYZ"
              onChange={(e) => setConfirmation(e.target.value.toUpperCase())} />
          </label>
          <label className="plan-field">
            <span className="plan-field-label">Provider</span>
            <div className="plan-chips">
              {PROVIDERS.map((p) => (
                <button key={p} type="button" className={`plan-chip-btn${provider === p ? ' active' : ''}`}
                  onClick={() => setProvider(p)}>{p}</button>
              ))}
            </div>
          </label>
          <div className="plan-field-row">
            <label className="plan-field">
              <span className="plan-field-label">Total cost</span>
              <input className="plan-input" value={cost} placeholder="320.00" onChange={(e) => setCost(e.target.value)} />
            </label>
            <label className="plan-field">
              <span className="plan-field-label">Currency</span>
              <input className="plan-input" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </label>
          </div>
          <label className="plan-field">
            <span className="plan-field-label">Notes</span>
            <textarea className="plan-input plan-textarea" rows={2} value={notes}
              placeholder="Seat 12A, gate, etc." onChange={(e) => setNotes(e.target.value)} />
          </label>
        </div>
        <div className="plan-modal-foot">
          <button className="plan-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="plan-btn-primary" disabled={saving || !confirmation.trim()} onClick={submit}>
            {saving ? 'Saving…' : 'Confirm booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlanDetail;
